import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShieldCheck, Lock, Eye, EyeOff, ChevronDown, Check,
  ArrowRight, Loader2, AlertCircle, UserCog, Minus, Plus, Languages,
} from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { OFFICERS, verifyPassword, positionInitials } from "../auth/officers";
import { usePreferences, FONT_SCALES } from "../context/PreferencesContext";

const COPY = {
  en: {
    brand: "Kumbh AI",
    textSize: "Text size",
    tagline: "AI COMMAND ASSURANCE PLATFORM",
    liveBadge: "LIVE OPS · 14 MODULES OPERATIONAL",
    headline1: "Unified command for",
    headline2: "Simhastha Kumbh 2027",
    heroSub: "Real-time crowd, risk & emergency intelligence across Nashik & Trimbakeshwar — one secure console for every command decision.",
    features: [
      "Live crowd & multi-hazard risk engine",
      "Inter-agency war room & field copilot",
      "Privacy-by-design · DPDP 2023 compliant",
    ],
    leftFooter: "Authorised personnel only · All access is logged · DPDP 2023 compliant",
    mobileTagline: "AI Command Assurance Platform",
    secureBadge: "Secure Console Access",
    signInHeading: "Sign in to continue",
    signInSub: "Select your officer profile and enter your access password.",
    officerLabel: "Officer Profile",
    officerPlaceholder: "Select officer profile",
    passwordLabel: "Access Password",
    passwordPlaceholder: "Enter access password",
    errNoOfficer: "Please select your officer profile.",
    errNoPassword: "Please enter your access password.",
    errWrongPassword: "Incorrect access password. Please try again.",
    verifying: "Verifying access…",
    signInBtn: "Sign In to Console",
    rightFooter: "Authorised personnel only · All access is logged",
    version: "Kumbh AI · v1.0 · Nashik & Trimbakeshwar",
  },
  mr: {
    brand: "कुंभ एआय",
    textSize: "मजकूर आकार",
    tagline: "एआय कमांड अ‍ॅश्युरन्स प्लॅटफॉर्म",
    liveBadge: "थेट कार्यरत · 14 मॉड्यूल्स कार्यरत",
    headline1: "सिंहस्थ कुंभ 2027 साठी",
    headline2: "एकीकृत कमांड",
    heroSub: "नाशिक व त्र्यंबकेश्वरमध्ये रिअल-टाइम गर्दी, जोखीम व आपत्कालीन माहिती — प्रत्येक कमांड निर्णयासाठी एक सुरक्षित कन्सोल.",
    features: [
      "थेट गर्दी व बहु-धोका जोखीम इंजिन",
      "आंतर-संस्था वॉर रूम व फील्ड कोपायलट",
      "गोपनीयता-रचित · डीपीडीपी 2023 अनुरूप",
    ],
    leftFooter: "केवळ अधिकृत कर्मचाऱ्यांसाठी · सर्व प्रवेशाची नोंद ठेवली जाते · डीपीडीपी 2023 अनुरूप",
    mobileTagline: "एआय कमांड अ‍ॅश्युरन्स प्लॅटफॉर्म",
    secureBadge: "सुरक्षित कन्सोल प्रवेश",
    signInHeading: "सुरू ठेवण्यासाठी प्रवेश करा",
    signInSub: "आपली अधिकारी प्रोफाइल निवडा आणि प्रवेश पासवर्ड टाका.",
    officerLabel: "अधिकारी प्रोफाइल",
    officerPlaceholder: "अधिकारी प्रोफाइल निवडा",
    passwordLabel: "प्रवेश पासवर्ड",
    passwordPlaceholder: "प्रवेश पासवर्ड टाका",
    errNoOfficer: "कृपया आपली अधिकारी प्रोफाइल निवडा.",
    errNoPassword: "कृपया आपला प्रवेश पासवर्ड टाका.",
    errWrongPassword: "चुकीचा प्रवेश पासवर्ड. कृपया पुन्हा प्रयत्न करा.",
    verifying: "प्रवेश पडताळत आहे…",
    signInBtn: "कन्सोलमध्ये प्रवेश करा",
    rightFooter: "केवळ अधिकृत कर्मचाऱ्यांसाठी · सर्व प्रवेशाची नोंद ठेवली जाते",
    version: "कुंभ एआय · आवृत्ती 1.0 · नाशिक व त्र्यंबकेश्वर",
  },
};

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { lang, setLang, scaleIdx, increaseText, decreaseText } = usePreferences();
  const c = COPY[lang];

  const [selected, setSelected] = useState(null); // officer object
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const dropRef = useRef(null);
  const pwRef = useRef(null);

  // Close the officer dropdown on outside-click / Escape.
  useEffect(() => {
    if (!dropOpen) return;
    const onClick = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setDropOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [dropOpen]);

  const pickOfficer = (o) => {
    setSelected(o);
    setDropOpen(false);
    setError("");
    setTimeout(() => pwRef.current?.focus(), 50); // fast-path to password entry
  };

  const submit = (e) => {
    e.preventDefault();
    if (loading) return;
    if (!selected) {
      setError(c.errNoOfficer);
      setDropOpen(true);
      return;
    }
    if (!password) {
      setError(c.errNoPassword);
      pwRef.current?.focus();
      return;
    }
    setError("");
    setLoading(true);
    /* The check is a salted SHA-256 comparison rather than a string
       equality against a constant in the bundle — see officers.js for why,
       and for why that is still not security. Web Crypto is async, so this
       is a promise rather than the setTimeout it used to be; the delay is
       kept so the button still reads as doing something. */
    setTimeout(async () => {
      const { ok, reason } = await verifyPassword(password);
      if (ok) {
        login(selected); // auth gate swaps Login → dashboard
        navigate("/", { replace: true }); // land on Command Overview, not a blank /login route
      } else {
        setLoading(false);
        /* A missing-crypto failure is not a wrong password, and telling the
           user it was would send them round in circles retyping a correct
           code. */
        setError(reason ?? c.errWrongPassword);
        setPassword("");
        pwRef.current?.focus();
      }
    }, 650);
  };

  return (
    <div className="min-h-screen w-full flex flex-col bg-slate-50">
      {/* ── Accessibility / utility bar — same pattern as the public landing page ── */}
      <div className="bg-slate-50 border-b border-slate-200 text-slate-600 flex-shrink-0">
        <div className="max-w-6xl mx-auto px-5 md:px-8 h-9 flex items-center justify-end gap-3 text-[11px] font-semibold">
          <span className="hidden sm:inline text-slate-400">{c.textSize}</span>
          <div className="flex items-center border border-slate-300 rounded-md overflow-hidden">
            <button
              type="button"
              aria-label="Decrease text size"
              onClick={decreaseText}
              disabled={scaleIdx === 0}
              className="w-6 h-6 flex items-center justify-center hover:bg-slate-200 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
            >
              <Minus size={11} />
            </button>
            <span className="w-6 h-6 flex items-center justify-center border-x border-slate-300 text-slate-700">A</span>
            <button
              type="button"
              aria-label="Increase text size"
              onClick={increaseText}
              disabled={scaleIdx === FONT_SCALES.length - 1}
              className="w-6 h-6 flex items-center justify-center hover:bg-slate-200 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
            >
              <Plus size={11} />
            </button>
          </div>
          <div className="flex items-center gap-1 border border-slate-300 rounded-md overflow-hidden">
            <Languages size={11} className="ml-2 text-slate-400" />
            <button
              type="button"
              onClick={() => setLang("en")}
              aria-pressed={lang === "en"}
              className={`px-2 h-6 transition-colors ${lang === "en" ? "bg-saffron-dark text-white" : "hover:bg-slate-200 text-slate-600"}`}
            >
              EN
            </button>
            <button
              type="button"
              onClick={() => setLang("mr")}
              aria-pressed={lang === "mr"}
              className={`px-2 h-6 transition-colors ${lang === "mr" ? "bg-saffron-dark text-white" : "hover:bg-slate-200 text-slate-600"}`}
            >
              मराठी
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 flex">
        {/* ── Left brand panel (saffron — matches header/landing colour) ─── */}
        <div className="hidden lg:flex lg:w-1/2 xl:w-[55%] relative bg-saffron overflow-hidden">
          {/* content */}
          <div className="relative z-10 flex flex-col justify-between h-full w-full px-12 xl:px-16 py-12 text-white">
            {/* brand */}
            <div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur-sm border border-white/25 flex items-center justify-center shadow-lg">
                  <span className="text-white font-black text-lg tracking-tight">K</span>
                </div>
                <div>
                  <div className="font-black text-xl leading-none tracking-tight">{c.brand}</div>
                  <div className="text-[11px] text-white/75 mt-1 tracking-[0.15em]">
                    {c.tagline}
                  </div>
                </div>
              </div>
              <div className="mt-4 inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/15 border border-white/25 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 live-dot" />
                <span className="text-[10px] font-black tracking-wider text-white">
                  {c.liveBadge}
                </span>
              </div>
            </div>

            {/* headline + features */}
            <div className="max-w-md">
              <h1 className="text-3xl xl:text-4xl font-black leading-tight tracking-tight">
                {c.headline1}
                <br />
                {c.headline2}
              </h1>
              <p className="mt-4 text-sm text-white/85 leading-relaxed">
                {c.heroSub}
              </p>
              <ul className="mt-7 space-y-3">
                {c.features.map((f) => (
                  <li key={f} className="flex items-center gap-3 text-sm text-white/90">
                    <span className="w-5 h-5 rounded-md bg-white/15 border border-white/25 flex items-center justify-center flex-shrink-0">
                      <Check size={12} />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
            </div>

            {/* footer */}
            <div>
              <div className="flex items-center gap-2 text-[11px] text-white/70">
                <ShieldCheck size={13} />
                <span>{c.leftFooter}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Right form panel ────────────────────────────────────── */}
        <div className="flex-1 flex items-center justify-center px-5 py-10 sm:px-8">
          <div className="w-full max-w-md">
            {/* mobile brand */}
            <div className="lg:hidden flex items-center justify-center gap-2.5 mb-8">
              <div className="w-10 h-10 rounded-xl gov-gradient flex items-center justify-center shadow-sm">
                <span className="text-white font-black text-sm">K</span>
              </div>
              <div>
                <div className="font-black text-base text-slate-900 leading-none">{c.brand}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">{c.mobileTagline}</div>
              </div>
            </div>

            {/* heading */}
            <div className="mb-7">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-saffron-light border border-saffron/30 rounded-full mb-4">
                <Lock size={11} className="text-saffron-dark" />
                <span className="text-[10px] font-black text-saffron-dark tracking-wider uppercase">
                  {c.secureBadge}
                </span>
              </div>
              <h2 className="text-2xl font-black text-slate-900 tracking-tight">{c.signInHeading}</h2>
              <p className="text-sm text-slate-500 mt-1.5">
                {c.signInSub}
              </p>
            </div>

            <form onSubmit={submit} className="space-y-5" noValidate>
              {/* Officer profile dropdown (username) */}
              <div ref={dropRef}>
                <label className="block text-[11px] font-black text-slate-500 uppercase tracking-wider mb-2">
                  {c.officerLabel}
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setDropOpen((o) => !o)}
                    aria-haspopup="listbox"
                    aria-expanded={dropOpen}
                    className={`w-full flex items-center gap-3 px-3.5 py-3 bg-white border rounded-xl text-left transition-all ${
                      dropOpen
                        ? "border-saffron ring-2 ring-saffron/15"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    {selected ? (
                      <>
                        <span
                          className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-xs font-black flex-shrink-0"
                          style={{
                            background: `linear-gradient(135deg, ${selected.accent[0]}, ${selected.accent[1]})`,
                          }}
                        >
                          {positionInitials(selected.position)}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-bold text-slate-900 leading-tight truncate">
                            {selected.position}
                          </span>
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                          <UserCog size={16} className="text-slate-400" />
                        </span>
                        <span className="flex-1 text-sm text-slate-400">{c.officerPlaceholder}</span>
                      </>
                    )}
                    <ChevronDown
                      size={16}
                      className={`text-slate-400 flex-shrink-0 transition-transform ${
                        dropOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  <AnimatePresence>
                    {dropOpen && (
                      <motion.ul
                        initial={{ opacity: 0, y: 6, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 6, scale: 0.98 }}
                        transition={{ duration: 0.15 }}
                        role="listbox"
                        className="absolute z-30 mt-2 w-full max-h-72 overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-xl py-1"
                      >
                        {OFFICERS.map((o) => {
                          const active = selected?.id === o.id;
                          return (
                            <li key={o.id} role="option" aria-selected={active}>
                              <button
                                type="button"
                                onClick={() => pickOfficer(o)}
                                className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                                  active ? "bg-saffron-light" : "hover:bg-slate-50"
                                }`}
                              >
                                <span
                                  className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-xs font-black flex-shrink-0"
                                  style={{
                                    background: `linear-gradient(135deg, ${o.accent[0]}, ${o.accent[1]})`,
                                  }}
                                >
                                  {positionInitials(o.position)}
                                </span>
                                <span className="flex-1 min-w-0">
                                  <span className="block text-sm font-bold text-slate-900 leading-tight truncate">
                                    {o.position}
                                  </span>
                                </span>
                                {active && (
                                  <Check size={16} className="text-saffron-dark flex-shrink-0" />
                                )}
                              </button>
                            </li>
                          );
                        })}
                      </motion.ul>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* Access password */}
              <div>
                <label className="block text-[11px] font-black text-slate-500 uppercase tracking-wider mb-2">
                  {c.passwordLabel}
                </label>
                <div className="relative">
                  <Lock
                    size={15}
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                  />
                  <input
                    ref={pwRef}
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (error) setError("");
                    }}
                    placeholder={c.passwordPlaceholder}
                    autoComplete="current-password"
                    className="w-full pl-10 pr-11 py-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 placeholder-slate-400 outline-none focus:border-saffron focus:ring-2 focus:ring-saffron/15 transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((s) => !s)}
                    tabIndex={-1}
                    aria-label={showPw ? "Hide password" : "Show password"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* error */}
              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="flex items-center gap-2 px-3.5 py-2.5 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 font-medium overflow-hidden"
                  >
                    <AlertCircle size={15} className="flex-shrink-0" />
                    <span>{error}</span>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl text-sm font-black text-white bg-saffron hover:brightness-95 shadow-lg transition-all disabled:opacity-70 disabled:cursor-not-allowed active:scale-[0.99]"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> {c.verifying}
                  </>
                ) : (
                  <>
                    {c.signInBtn} <ArrowRight size={16} />
                  </>
                )}
              </button>
            </form>

            {/* footer */}
            <div className="mt-8 flex items-center justify-center gap-2 text-[11px] text-slate-400">
              <ShieldCheck size={12} />
              <span>{c.rightFooter}</span>
            </div>
            <div className="mt-2 text-center text-[10px] text-slate-300">
              {c.version}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
